import React from 'react';
import { 
  FaWallet, FaChartPie, FaCreditCard, FaShieldAlt, FaBullseye,
  FaCalendarAlt, FaCheckCircle, FaEdit, FaHeart, FaHeartbeat
} from 'react-icons/fa';
import InsuranceGapCard from '../shared/InsuranceGapCard';
import GoalProgressCard from '../shared/GoalProgressCard';

const ClientFinancialSummary = ({ financialSummary, formatCurrency }) => {
  if (!financialSummary?.has_finalized_report) return null;

  return (
    <div className="space-y-6" data-testid="final-report-dashboard">
      {/* Next Review Date Banner */}
      {financialSummary.next_review_date && (
        <div className="bg-gradient-to-r from-[#FFD700]/20 to-[#D4AF37]/20 rounded-xl p-4 border border-[#FFD700]/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FaCalendarAlt className="text-[#FFD700] text-2xl" />
              <div>
                <p className="text-[#FFD700] font-bold">Next Review Date</p>
                <p className="text-white text-lg">{new Date(financialSummary.next_review_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
              </div>
            </div>
            <span className="px-3 py-1 bg-[#FFD700]/20 text-[#FFD700] rounded-full text-sm">Schedule a call with your advisor</span>
          </div>
        </div>
      )}

      {/* Financial Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="financial-summary">
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#FFD700]/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Net Worth</p>
              <p className="text-2xl font-bold text-[#FFD700]">{formatCurrency(financialSummary.net_worth)}</p>
            </div>
            <FaWallet className="text-[#FFD700] text-3xl opacity-50" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-green-500/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Investments</p>
              <p className="text-2xl font-bold text-green-400">{formatCurrency(financialSummary.total_investments)}</p>
            </div>
            <FaChartPie className="text-green-400 text-3xl opacity-50" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-red-500/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Total Liabilities</p>
              <p className="text-2xl font-bold text-red-400">{formatCurrency(financialSummary.total_liabilities)}</p>
            </div>
            <FaCreditCard className="text-red-400 text-3xl opacity-50" />
          </div>
        </div>
      </div>

      {/* Insurance & Protection Gaps */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center">
          <FaShieldAlt className="mr-2 text-[#FFD700]" /> Insurance & Protection Analysis
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Life Insurance */}
          <InsuranceGapCard
            title="Life Insurance"
            icon={FaHeart}
            current={financialSummary.life_insurance_current}
            required={financialSummary.life_insurance_required}
            gap={financialSummary.life_insurance_gap}
            formatCurrency={formatCurrency}
            color="red"
          />
          
          {/* Health Insurance */}
          <InsuranceGapCard
            title="Health Insurance"
            icon={FaHeartbeat}
            current={financialSummary.health_insurance_current}
            required={financialSummary.health_insurance_required}
            gap={financialSummary.health_insurance_gap}
            formatCurrency={formatCurrency}
            color="pink"
          />
        </div>
      </div>

      {/* Goals Summary with Progress */}
      {financialSummary.goals_summary && financialSummary.goals_summary.length > 0 && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white flex items-center">
              <FaBullseye className="mr-2 text-[#FFD700]" /> Goals Progress & Gap Analysis
            </h3>
            <div className="text-right">
              <p className="text-gray-400 text-sm">Total Monthly SIP Required</p>
              <p className="text-xl font-bold text-[#FFD700]">{formatCurrency(financialSummary.total_monthly_commitment)}</p>
            </div>
          </div>
          
          <div className="space-y-4">
            {financialSummary.goals_summary.map((goal, index) => (
              <GoalProgressCard key={index} goal={goal} formatCurrency={formatCurrency} />
            ))}
          </div>
        </div>
      )}

      {/* Key Action Items */}
      {financialSummary.key_action_items && financialSummary.key_action_items.length > 0 && (
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-6 border border-[#2D5A8F]">
          <h3 className="text-lg font-bold text-white mb-4 flex items-center">
            <FaCheckCircle className="mr-2 text-green-400" /> Recommended Action Items
          </h3>
          <div className="space-y-3">
            {financialSummary.key_action_items.map((item, index) => (
              <div key={index} className="flex items-start space-x-3 p-3 bg-[#0F172A] rounded-lg">
                <span className="flex-shrink-0 w-6 h-6 bg-[#FFD700] text-[#0A1929] rounded-full flex items-center justify-center text-sm font-bold">
                  {index + 1}
                </span>
                <p className="text-gray-300">{item}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Advisor Notes */}
      {financialSummary.advisor_notes && (
        <div className="bg-gradient-to-r from-blue-900/30 to-blue-800/30 rounded-xl p-4 border border-blue-500/50">
          <h4 className="text-blue-400 font-bold mb-2 flex items-center">
            <FaEdit className="mr-2" /> Note from Your Advisor
          </h4>
          <p className="text-gray-300">{financialSummary.advisor_notes}</p>
        </div>
      )}
    </div>
  );
};

export default ClientFinancialSummary;
